/**
 * DirectHitStrategy - Direct Hit Frame Navigation (Holy Red Dot Edition)
 *
 * Enhanced with configurable filters:
 * - 3D distance filtering
 * - Delta angle filtering
 * - Connectivity filtering
 * - Fallback strategies
 *
 * Core behavior:
 * 1. The holy red dot is at screen center, pointing to world coordinates
 * 2. All frames have world positions with Frame 0 center at (0, 0)
 * 3. Calculate which world coordinate is under the red dot
 * 4. Apply filters (connectivity, 3D distance, delta angle)
 * 5. Find nearest frame among valid candidates
 * 6. If no candidates, apply fallback strategy
 * 7. Auto-select frame when distance < threshold
 *
 * Key: Uses world coordinates and Euclidean distance from the holy red dot reference
 */

import { NavigationSettings, SelectionDebugInfo, FrameFilterMetadata } from '../types/NavigationSettings'
import {
  applyFilters,
  applyFallbackFilters,
  convertThresholdPercentToPixels,
} from '../utils/navigationFilters'

export interface FrameWorldPosition {
  frameIndex: number
  worldX: number  // World coordinate X
  worldY: number  // World coordinate Y
}

export interface RedDotReference {
  worldX: number  // World coordinate under red dot
  worldY: number  // World coordinate under red dot
}

export interface FrameDistanceMetric {
  frameIndex: number
  worldX: number
  worldY: number
  dx: number  // Offset from red dot X
  dy: number  // Offset from red dot Y
  distance: number  // Euclidean distance from red dot
}

export interface SelectionResult {
  selectedFrame: number
  reason: string
  nearestFrameDistance: number
  confidence: number
  hasChanged: boolean  // Did selection actually change?
  jumpDistance?: number  // 🆕 Number of frames jumped (for animation)
  debugInfo?: SelectionDebugInfo  // Optional debug information
}

export class DirectHitStrategy {
  private lastSelectedFrame: number | null = null
  private distanceThresholdPercent: number = 20  // 20% of frame width
  private frameWidth: number = 450  // Standard frame width
  private navigationSettings: NavigationSettings | null = null
  private lastSelectionTime: number = 0
  private selectionCooldownMs: number = 150

  constructor(
    private connectivityGraph: Map<number, Set<number>>,
    settings?: NavigationSettings
  ) {
    this.navigationSettings = settings || null
  }

  /**
   * Update navigation settings (called when settings change)
   */
  setNavigationSettings(settings: NavigationSettings): void {
    this.navigationSettings = settings
    this.distanceThresholdPercent = settings.distanceThresholdPercent
  }

  /**
   * Main selection method - called with holy red dot reference
   *
   * @param frameWorldPositions All frame positions in world space
   * @param redDotReference Current world position of the holy red dot
   * @param currentFrameIndex Currently selected frame index
   * @param imageDimensions Frame display dimensions for calculating tolerance radius
   * @param frameMetadata Optional metadata for filtering (3D distance, angle, etc)
   * @param restrictToConnected Deprecated - for backwards compatibility
   * @param consecutiveAutoReturns Number of consecutive auto-return attempts (for progressive weakening)
   * @param settings Navigation settings (passed as parameter)
   * @param trigger Type of selection trigger (pan-move, pan-end, auto) - pan-end bypasses cooldown
   */
  selectFrameByRedDot(
    frameWorldPositions: FrameWorldPosition[],
    redDotReference: RedDotReference,
    currentFrameIndex: number,
    imageDimensions: { displayWidth: number; displayHeight: number } | undefined,
    frameMetadata: Map<number, FrameFilterMetadata> | undefined,
    restrictToConnected: boolean | undefined,
    consecutiveAutoReturns: number,
    settings: NavigationSettings,
    trigger: 'pan-move' | 'pan-end' | 'auto' = 'pan-move',
    panMotionVector?: { x: number; y: number } | null,
    accumulatedPanDistance?: number  // 🆕 NEW: accumulated distance to prevent oscillation
  ): SelectionResult {
    console.log(`\n[DirectHitStrategy.selectFrameByRedDot] CALLED (trigger: ${trigger})`)
    console.log(`   Red dot: (${redDotReference.worldX.toFixed(1)}, ${redDotReference.worldY.toFixed(1)})`)
    console.log(`   Current frame: ${currentFrameIndex}`)
    console.log(`   Total frames: ${frameWorldPositions.length}`)
    
    if (frameWorldPositions.length === 0) {
      console.log(`   ❌ No frames available`)
      return {
        selectedFrame: currentFrameIndex,
        reason: 'No frames available',
        nearestFrameDistance: Infinity,
        confidence: 0,
        hasChanged: false,
      }
    }

    // Guard against undefined imageDimensions
    if (!imageDimensions) {
      return {
        selectedFrame: currentFrameIndex,
        reason: 'Image dimensions not available, keeping current frame',
        nearestFrameDistance: 0,
        confidence: 0.0,
        hasChanged: false,
      }
    }

    // 🆕 DIRECTIONAL SELECTION: Check for pan motion-based selection BEFORE sticky check
    // This allows smooth sequential navigation even within sticky bounds
    if (
      trigger === 'pan-move' &&
      settings.enableDirectionalPanSelection &&
      panMotionVector &&
      imageDimensions
    ) {
      console.log(`[DirectHitStrategy] 🧭 Checking directional selection: panMotion=(${panMotionVector.x.toFixed(1)}, ${panMotionVector.y.toFixed(1)}), accumulatedDistance=${accumulatedPanDistance?.toFixed(1) || 0}`)
      
      const directionalResult = this.selectFrameDirectional(
        frameWorldPositions,
        currentFrameIndex,
        redDotReference,
        panMotionVector,
        accumulatedPanDistance  // 🆕 pass accumulated distance to directional selection
      )
      
      if (directionalResult) {
        this.lastSelectedFrame = directionalResult.selectedFrame
        this.lastSelectionTime = Date.now()
        console.log(`[DirectHitStrategy] ✅ Directional selection applied: ${currentFrameIndex} → ${directionalResult.selectedFrame}`)
        return directionalResult
      } else {
        console.log(`[DirectHitStrategy] ❌ No viable directional candidate, falling through to normal logic`)
      }
    }

    // Check sticky selection: if enabled and red dot is within elliptical tolerance of current frame center
    // Use elliptical bounds based on full frame dimensions for accurate aspect ratio matching
    // Progressive weakening: reduce tolerance after consecutive auto-return attempts
    // SKIP for pan-end: user explicitly wants to navigate, so don't block with sticky
    if (settings.useStickyFrameSelection && trigger !== 'pan-end') {
      const currentFramePos = frameWorldPositions.find(pos => pos.frameIndex === currentFrameIndex)
      if (currentFramePos) {
        // Calculate base semi-axes for elliptical tolerance
        const baseSemiWidth = (imageDimensions.displayWidth * settings.stickyRatio) / 2
        const baseSemiHeight = (imageDimensions.displayHeight * settings.stickyRatio) / 2
        
        // Progressive weakening based on consecutive auto-return attempts
        // Attempt 0: 100%, Attempt 1: 100%, Attempt 2: 70%, Attempt 3: 40%, Attempt 4+: 0% (disabled)
        let weakeningFactor = 1.0
        if (settings.useProgressiveWeakeningAndHysteresis) {
          if (consecutiveAutoReturns >= 4) {
            weakeningFactor = 0 // Disable sticky completely after 4th attempt
          } else if (consecutiveAutoReturns === 3) {
            weakeningFactor = 0.4 // 40% on 3rd attempt
          } else if (consecutiveAutoReturns === 2) {
            weakeningFactor = 0.7 // 70% on 2nd attempt
          }
          // Attempt 0-1: 100% (first pan and first retry use full sticky)
        }
        
        const effectiveSemiWidth = baseSemiWidth * weakeningFactor
        const effectiveSemiHeight = baseSemiHeight * weakeningFactor
        
        // Calculate offsets from red dot to frame center
        const dx = redDotReference.worldX - currentFramePos.worldX
        const dy = redDotReference.worldY - currentFramePos.worldY
        
        // Check if point is within ellipse: (dx/semiWidth)^2 + (dy/semiHeight)^2 <= 1
        const isWithinEllipse = (dx * dx) / (effectiveSemiWidth * effectiveSemiWidth) + (dy * dy) / (effectiveSemiHeight * effectiveSemiHeight) <= 1

        if (isWithinEllipse) {
          const weakeningNote = consecutiveAutoReturns > 0 
            ? ` [weakened: attempt ${consecutiveAutoReturns + 1}, ${(weakeningFactor * 100).toFixed(0)}% of base ${baseSemiWidth.toFixed(1)}x${baseSemiHeight.toFixed(1)}px]`
            : ''
          // ADD: Log when inside ellipse
          // console.log(`[Sticky] INSIDE ellipse: Red dot at (${redDotReference.worldX.toFixed(1)}, ${redDotReference.worldY.toFixed(1)}) within ${effectiveSemiWidth.toFixed(1)}x${effectiveSemiHeight.toFixed(1)}px ellipse of frame ${currentFrameIndex} center (${currentFramePos.worldX.toFixed(1)}, ${currentFramePos.worldY.toFixed(1)}) - KEEPING CURRENT${weakeningNote}`)
          return {
            selectedFrame: currentFrameIndex,
            reason: `Sticky selection: Red dot within ellipse of frame ${currentFrameIndex} center (tolerance: ${effectiveSemiWidth.toFixed(1)}x${effectiveSemiHeight.toFixed(1)}px ellipse)${weakeningNote}`,
            nearestFrameDistance: Math.sqrt(dx * dx + dy * dy), // Keep for compatibility
            confidence: 1.0,
            hasChanged: false,
          }
        } else {
          // ADD: Log when outside ellipse
          console.log(`[Sticky] OUTSIDE ellipse: Red dot at (${redDotReference.worldX.toFixed(1)}, ${redDotReference.worldY.toFixed(1)}) outside ${effectiveSemiWidth.toFixed(1)}x${effectiveSemiHeight.toFixed(1)}px ellipse of frame ${currentFrameIndex} center (${currentFramePos.worldX.toFixed(1)}, ${currentFramePos.worldY.toFixed(1)}) - PROCEEDING TO SELECTION`)
        }
      }
    }

    // Calculate distance metrics for all frames
    const frameMetrics = frameWorldPositions.map(framePos =>
      this.calculateDistanceFromRedDot(framePos, redDotReference)
    )

    // Apply cooldown only for pan-move and auto triggers, NOT for pan-end
    // Pan-end is explicit user-driven navigation and should select immediately
    const now = Date.now()
    if (trigger !== 'pan-end' && now - this.lastSelectionTime < this.selectionCooldownMs) {
      const nearest = frameMetrics.reduce((a, b) => (a.distance < b.distance ? a : b))
      console.log(`[DirectHitStrategy] ⏱️ COOLDOWN ACTIVE (${trigger}): Last selection was ${(now - this.lastSelectionTime).toFixed(0)}ms ago (cooldown: ${this.selectionCooldownMs}ms)`)
      console.log(`   Nearest frame would be: ${nearest.frameIndex} at ${nearest.distance.toFixed(1)}px, but blocked by cooldown`)
      return {
        selectedFrame: this.lastSelectedFrame ?? currentFrameIndex,
        reason: 'Selection cooldown active',
        nearestFrameDistance: nearest.distance,
        confidence: 0,
        hasChanged: false,
      }
    }

    if (trigger === 'pan-end' && now - this.lastSelectionTime < this.selectionCooldownMs) {
      console.log(`[DirectHitStrategy] ✅ BYPASSING COOLDOWN for pan-end (Last selection was ${(now - this.lastSelectionTime).toFixed(0)}ms ago)`)
    }

    // Apply filters if settings available
    let validMetrics = frameMetrics
    let fallbackApplied = false
    let fallbackReason: string | undefined

    if (this.navigationSettings) {
      // Prepare frame metadata for filtering
      const candidates: FrameFilterMetadata[] = frameMetrics.map(metric => {
        // Get frame metadata if available, handle null gracefully
        const metadata = frameMetadata?.get(metric.frameIndex)
        return {
          frameIndex: metric.frameIndex,
          distance3d: metadata?.distance3d,
          angleDiff: metadata?.angleDiff,
          isConnected:
            restrictToConnected === true
              ? // Old API compatibility
                (this.connectivityGraph?.get(currentFrameIndex)?.has(metric.frameIndex) ?? false) ||
                metric.frameIndex === currentFrameIndex
              : // New API: use settings
              this.navigationSettings && this.navigationSettings.useConnectivityFiltering
              ? (this.connectivityGraph?.get(currentFrameIndex)?.has(metric.frameIndex) ?? false) ||
                metric.frameIndex === currentFrameIndex
              : true,
        }
      }).filter(c => c !== null) as FrameFilterMetadata[]  // Ensure no null candidates

      // Apply filters
      let filterResult = applyFilters(candidates, this.navigationSettings, currentFrameIndex)

      // Log filter results for debugging
      if (filterResult.validFrames.length === 0) {
        console.warn('🔴 All frames rejected by filters')
        filterResult.rejectionReasons.forEach((reasons, frameIndex) => {
          console.warn(`  Frame ${frameIndex}: ${reasons.join(', ')}`)
        })
      }

      // If no valid candidates, apply fallback
      if (
        filterResult.validFrames.length === 0 &&
        this.navigationSettings.fallbackStrategy !== 'none'
      ) {
        if (this.navigationSettings.fallbackStrategy === 'less-restrictive') {
          const fallbackResult = applyFallbackFilters(candidates, this.navigationSettings, currentFrameIndex)
          filterResult = fallbackResult.result
          fallbackApplied = fallbackResult.reason !== undefined
          fallbackReason = fallbackResult.reason
        } else if (this.navigationSettings.fallbackStrategy === 'unrestricted-2d') {
          // Already have unfiltered metrics, just use all
          fallbackApplied = true
          fallbackReason = 'Unrestricted 2D selection'
        }
      }

      // Filter metrics to only valid frames
      validMetrics = frameMetrics.filter(m => filterResult.validFrames.includes(m.frameIndex))
    } else if (restrictToConnected === true) {
      // Backwards compatibility: old API without settings
      const connectedIndices = this.connectivityGraph?.get(currentFrameIndex) || new Set()
      const validIndices = new Set([currentFrameIndex, ...connectedIndices])
      validMetrics = frameMetrics.filter(m => validIndices.has(m.frameIndex))
    }

    // Handle no valid frames
    if (validMetrics.length === 0) {
      // ADD: Log if no valid frames after filters
      console.log(`[Selection] No valid frames after filtering - staying on current frame ${currentFrameIndex}`)
      return {
        selectedFrame: this.lastSelectedFrame ?? currentFrameIndex,
        reason: 'No valid candidates after filtering',
        nearestFrameDistance: frameMetrics[0]?.distance ?? Infinity,
        confidence: 0,
        hasChanged: false,
        debugInfo: {
          selectedFrame: this.lastSelectedFrame ?? currentFrameIndex,
          reason: 'No valid candidates after filtering',
          confidence: 0,
          distance2d: frameMetrics[0]?.distance ?? Infinity,
          hasChanged: false,
          filtersUsed: this.getActiveFilters(),
          fallbackApplied,
          fallbackReason,
        },
      }
    }

    // Find nearest frame
    const nearest = validMetrics.reduce((a, b) => (a.distance < b.distance ? a : b))

    // Determine if we should select this frame
    const distanceThresholdPx = convertThresholdPercentToPixels(
      this.distanceThresholdPercent,
      this.frameWidth
    )
    const shouldSelect = nearest.distance < distanceThresholdPx
    
    console.log(`[DirectHitStrategy] 🎯 Nearest frame: ${nearest.frameIndex} at ${nearest.distance.toFixed(1)}px`)
    console.log(`   Distance threshold: ${distanceThresholdPx.toFixed(1)}px, shouldSelect: ${shouldSelect}`)
    
    // SIMPLIFIED: Disabled hysteresis - just select nearest frame
    // (Hysteresis was preventing correct frame selection, oscillation cause is elsewhere)
    let hasChanged = shouldSelect && this.lastSelectedFrame !== nearest.frameIndex

    if (shouldSelect && hasChanged) {
      this.lastSelectedFrame = nearest.frameIndex
      this.lastSelectionTime = now
      const metadata = frameMetadata?.get(nearest.frameIndex)
      console.log(`[DirectHitStrategy] ✅ SELECTING: Frame ${nearest.frameIndex} (nearest at ${nearest.distance.toFixed(1)}px)`)
      // 🆕 Calculate jump distance for animation
      const jumpDistance = Math.abs(nearest.frameIndex - currentFrameIndex)
      return {
        selectedFrame: nearest.frameIndex,
        reason: `Frame ${nearest.frameIndex} is nearest to red dot (distance: ${nearest.distance.toFixed(1)}px)`,
        nearestFrameDistance: nearest.distance,
        confidence: Math.max(0, 1 - nearest.distance / distanceThresholdPx),
        hasChanged,
        jumpDistance,  // 🆕 Add jump distance for animation trigger
        debugInfo: {
          selectedFrame: nearest.frameIndex,
          reason: `Frame ${nearest.frameIndex} is nearest to red dot`,
          confidence: Math.max(0, 1 - nearest.distance / distanceThresholdPx),
          distance2d: nearest.distance,
          distance3d: metadata?.distance3d,
          angleDiff: metadata?.angleDiff,
          hasChanged,
          filtersUsed: this.getActiveFilters(),
          fallbackApplied,
          fallbackReason,
        },
      }
    } else if (shouldSelect && !hasChanged) {
      // shouldSelect but no change (same frame or blocked by hysteresis)
      this.lastSelectedFrame = nearest.frameIndex
      const metadata = frameMetadata?.get(nearest.frameIndex)
      // 🆕 Calculate jump distance (will be 0 if no change)
      const jumpDistance = Math.abs(nearest.frameIndex - currentFrameIndex)
      return {
        selectedFrame: nearest.frameIndex,
        reason: `Staying on frame ${nearest.frameIndex} (no change or blocked by hysteresis)`,
        nearestFrameDistance: nearest.distance,
        confidence: Math.max(0, 1 - nearest.distance / distanceThresholdPx),
        hasChanged: false,
        jumpDistance,  // 🆕 Add jump distance (will be 0)
        debugInfo: {
          selectedFrame: nearest.frameIndex,
          reason: `Staying on frame ${nearest.frameIndex}`,
          confidence: Math.max(0, 1 - nearest.distance / distanceThresholdPx),
          distance2d: nearest.distance,
          distance3d: metadata?.distance3d,
          angleDiff: metadata?.angleDiff,
          hasChanged: false,
          filtersUsed: this.getActiveFilters(),
          fallbackApplied,
          fallbackReason,
        },
      }
    }

    // EMPTY AREA CHECK: No frame close enough to red dot
    // Selection is NOT allowed in empty areas - block any selection attempt
    if (!shouldSelect) {
      console.log(`[DirectHitStrategy] 🚫 Empty area: blocking selection (nearest frame ${nearest.frameIndex} at distance ${nearest.distance.toFixed(1)}px exceeds threshold ${distanceThresholdPx.toFixed(1)}px)`)
      return {
        selectedFrame: this.lastSelectedFrame ?? currentFrameIndex,
        reason: `Empty area (no frame within threshold) → blocking selection`,
        nearestFrameDistance: nearest.distance,
        confidence: 0,
        hasChanged: false,
      }
    }

    // Inside sticky or no eligible frames - stay on current
    const metadata = frameMetadata?.get(nearest.frameIndex)
    // ADD: Log final result
    console.log(`[Selection] Final result: NO CHANGE to frame ${this.lastSelectedFrame ?? currentFrameIndex} - reason: Nearest frame ${nearest.frameIndex} too far`)
    return {
      selectedFrame: this.lastSelectedFrame ?? currentFrameIndex,
      reason: `Nearest frame ${nearest.frameIndex} too far (distance: ${nearest.distance.toFixed(1)}px, threshold: ${distanceThresholdPx.toFixed(1)}px)`,
      nearestFrameDistance: nearest.distance,
      confidence: 0,
      hasChanged: false,
      debugInfo: {
        selectedFrame: this.lastSelectedFrame ?? currentFrameIndex,
        reason: `Too far (${nearest.distance.toFixed(1)}px > ${distanceThresholdPx.toFixed(1)}px)`,
        confidence: 0,
        distance2d: nearest.distance,
        distance3d: metadata?.distance3d,
        angleDiff: metadata?.angleDiff,
        hasChanged: false,
        filtersUsed: this.getActiveFilters(),
        fallbackApplied,
        fallbackReason,
      },
    }
  }

  /**
   * Calculate distance metrics from holy red dot to a frame
   *
   * Metrics:
   * - dx, dy: Offset from red dot to frame center
   * - distance: Euclidean distance
   */
  private calculateDistanceFromRedDot(
    framePos: FrameWorldPosition,
    redDotRef: RedDotReference
  ): FrameDistanceMetric {
    const dx = framePos.worldX - redDotRef.worldX
    const dy = framePos.worldY - redDotRef.worldY
    const distance = Math.sqrt(dx * dx + dy * dy)

    return {
      frameIndex: framePos.frameIndex,
      worldX: framePos.worldX,
      worldY: framePos.worldY,
      dx,
      dy,
      distance,
    }
  }

  /**
   * Try directional selection based on pan motion
   * 
   * Checks n±1, n±2, n±3 frames in the direction of pan motion.
   * Only selects if:
   * 1. Pan motion is meaningful (>15px)
   * 2. Candidate is aligned with pan direction (dot product > 0.3)
   * 3. Candidate is close enough (distance < current × 1.2)
   * 
   * Returns result if viable directional candidate found, null otherwise
   */
  private selectFrameDirectional(
    frameWorldPositions: FrameWorldPosition[],
    currentFrameIndex: number,
    redDotReference: RedDotReference,
    panMotionVector: { x: number; y: number },
    accumulatedPanDistance?: number  // 🆕 NEW: accumulated distance to prevent oscillation
  ): SelectionResult | null {
    const MIN_PAN_DISTANCE = 10  // Require 15+ pixels of pan motion
    const ACCUMULATED_MIN_DISTANCE = 10  // 🆕 Also require 15+ pixels accumulated from frame selection
    const ALIGNMENT_THRESHOLD = 0.3  // 30% alignment with motion direction
    const DISTANCE_RATIO = 1.2  // Allow up to 20% farther than current distance
    const MAX_FRAME_OFFSET = 3  // Check up to n±3 frames

    // Check if pan motion is meaningful
    const panDistance = Math.sqrt(panMotionVector.x ** 2 + panMotionVector.y ** 2)
    if (panDistance < MIN_PAN_DISTANCE) {
      return null  // Pan too small, skip directional logic
    }

    // 🆕 ALSO check if accumulated distance is meaningful (prevents oscillation at boundaries)
    if (!accumulatedPanDistance || accumulatedPanDistance < ACCUMULATED_MIN_DISTANCE) {
      console.log(`[selectFrameDirectional] ❌ Insufficient accumulated distance: ${accumulatedPanDistance?.toFixed(1) || 0} < ${ACCUMULATED_MIN_DISTANCE}`)
      return null
    }

    const currentFramePos = frameWorldPositions.find(f => f.frameIndex === currentFrameIndex)
    if (!currentFramePos) return null

    const currentDistance = Math.sqrt(
      (currentFramePos.worldX - redDotReference.worldX) ** 2 +
      (currentFramePos.worldY - redDotReference.worldY) ** 2
    )

    // Helper: dot product for alignment calculation
    const dotProduct = (a: { x: number; y: number }, b: { x: number; y: number }) =>
      a.x * b.x + a.y * b.y

    // Helper: normalize vector
    const normalize = (v: { x: number; y: number }) => {
      const len = Math.sqrt(v.x ** 2 + v.y ** 2)
      return len === 0 ? { x: 0, y: 0 } : { x: v.x / len, y: v.y / len }
    }

    const panMotionNormalized = normalize(panMotionVector)

    // Collect viable candidates from both directions
    const candidates: Array<{ frameIndex: number; alignment: number; distance: number }> = []

    // Check forward direction (n+1, n+2, n+3)
    for (let offset = 1; offset <= MAX_FRAME_OFFSET; offset++) {
      const candidateIndex = currentFrameIndex + offset
      const candidatePos = frameWorldPositions.find(f => f.frameIndex === candidateIndex)
      
      if (!candidatePos) break  // Frame doesn't exist

      const vectorToCandidate = {
        x: candidatePos.worldX - currentFramePos.worldX,
        y: candidatePos.worldY - currentFramePos.worldY
      }

      const vectorNormalized = normalize(vectorToCandidate)
      const alignment = dotProduct(panMotionNormalized, vectorNormalized)
      const distanceToCandidate = Math.sqrt(
        (candidatePos.worldX - redDotReference.worldX) ** 2 +
        (candidatePos.worldY - redDotReference.worldY) ** 2
      )

      if (alignment > ALIGNMENT_THRESHOLD && distanceToCandidate < currentDistance * DISTANCE_RATIO) {
        candidates.push({ frameIndex: candidateIndex, alignment, distance: distanceToCandidate })
      }
    }

    // Check backward direction (n-1, n-2, n-3)
    for (let offset = 1; offset <= MAX_FRAME_OFFSET; offset++) {
      const candidateIndex = currentFrameIndex - offset
      if (candidateIndex < 0) break  // Out of bounds

      const candidatePos = frameWorldPositions.find(f => f.frameIndex === candidateIndex)
      if (!candidatePos) break

      const vectorToCandidate = {
        x: candidatePos.worldX - currentFramePos.worldX,
        y: candidatePos.worldY - currentFramePos.worldY
      }

      const vectorNormalized = normalize(vectorToCandidate)
      const alignment = dotProduct(panMotionNormalized, vectorNormalized)
      const distanceToCandidate = Math.sqrt(
        (candidatePos.worldX - redDotReference.worldX) ** 2 +
        (candidatePos.worldY - redDotReference.worldY) ** 2
      )

      if (alignment > ALIGNMENT_THRESHOLD && distanceToCandidate < currentDistance * DISTANCE_RATIO) {
        candidates.push({ frameIndex: candidateIndex, alignment, distance: distanceToCandidate })
      }
    }

    if (candidates.length === 0) return null  // No viable directional candidate

    // Pick best candidate: highest alignment, then closest if tied
    const bestCandidate = candidates.sort((a, b) =>
      b.alignment - a.alignment || a.distance - b.distance
    )[0]

    // 🆕 DISTANCE HYSTERESIS: Prevent oscillation at frame boundaries
    // Only select if red dot is significantly closer to candidate than current frame
    // Ratio = distance_to_candidate / (distance_to_current + distance_to_candidate)
    // Must be < 40% to prevent oscillation when perpendicular to frame connection line
    const DISTANCE_HYSTERESIS_THRESHOLD = 0.4
    const distanceRatio = bestCandidate.distance / (currentDistance + bestCandidate.distance)
    
    console.log(`[DirectHitStrategy] 🧭 DIRECTIONAL: Frame ${bestCandidate.frameIndex} candidate (alignment: ${bestCandidate.alignment.toFixed(2)}, distance: ${bestCandidate.distance.toFixed(1)}px, current: ${currentDistance.toFixed(1)}px, ratio: ${distanceRatio.toFixed(2)})`)
    
    if (distanceRatio > DISTANCE_HYSTERESIS_THRESHOLD) {
      console.log(`[selectFrameDirectional] ❌ Distance hysteresis blocked: ratio ${distanceRatio.toFixed(2)} > ${DISTANCE_HYSTERESIS_THRESHOLD} (red dot too close to current frame at boundary)`)
      return null
    }

    console.log(`[DirectHitStrategy] ✅ Distance hysteresis passed: ratio ${distanceRatio.toFixed(2)} ≤ ${DISTANCE_HYSTERESIS_THRESHOLD}`)

    const jumpDistance = Math.abs(bestCandidate.frameIndex - currentFrameIndex)

    return {
      selectedFrame: bestCandidate.frameIndex,
      reason: `Directional selection: frame ${bestCandidate.frameIndex} aligned with pan motion (alignment: ${bestCandidate.alignment.toFixed(2)}, distance ratio: ${distanceRatio.toFixed(2)})`,
      nearestFrameDistance: bestCandidate.distance,
      confidence: bestCandidate.alignment,
      hasChanged: true,
      jumpDistance,  // 🆕 Return how many frames jumped
    }
  }

  /**
   * Get list of active filters for debugging
   */
  private getActiveFilters(): string[] {
    if (!this.navigationSettings) return []

    const filters: string[] = []
    if (this.navigationSettings.useConnectivityFiltering) filters.push('connectivity')
    if (this.navigationSettings.use3DDistanceFilter) filters.push('3D distance')
    if (this.navigationSettings.useDeltaAngleFilter) filters.push('delta angle')
    if (this.navigationSettings.maxCandidates !== null) filters.push('max candidates')
    return filters
  }

  /**
   * Set distance threshold percent
   */
  setDistanceThresholdPercent(percent: number): void {
    this.distanceThresholdPercent = percent
  }

  /**
   * Get current distance threshold percent
   */
  getDistanceThresholdPercent(): number {
    return this.distanceThresholdPercent
  }

  /**
   * Reset state
   */
  resetState(): void {
    this.lastSelectedFrame = null
  }

  /**
   * Get last selected frame (for debugging)
   */
  getLastSelectedFrame(): number | null {
    return this.lastSelectedFrame
  }

  /**
   * Get current navigation settings
   */
  getNavigationSettings(): NavigationSettings | null {
    return this.navigationSettings
  }
}
